import { B2C_LISTINGS_SORT_VALUES, LOCATION_MODE_VALUES, LOCATION_RADIUS_VALUES, } from '@/api/network/listings/requests/GetListingsRequest';
import { getListingFeatureRequestValues, getListingPropertyTypeRequestValues, } from '@/widgets/listingsBuy/listingsFilterOptions';
import { LOCATION_FILTER_TYPES, } from '@/api/network/locationSuggestions/types/locationSuggestion';
const CANONICAL_QUERY_KEYS = [
    'deal',
    'propertyTypes',
    'features',
    'priceMin',
    'priceMax',
    'areaMin',
    'areaMax',
    'roomsMin',
    'roomsMax',
    'bathroomsMin',
    'bathroomsMax',
    'municipality',
    'locality',
    'city',
    'district',
    'postalCode',
    'province',
    'provinces',
    'provinceCode',
    'region',
    'countryCode',
    'locationLabel',
    'locationType',
    'lat',
    'lng',
    'radiusKm',
    'locationMode',
    'uiLocationLabel',
    'uiCenterLat',
    'uiCenterLng',
    'page',
    'sort',
];
const LEGACY_QUERY_KEYS = ['dealType', 'propertyType', 'minPrice', 'maxPrice'];
const MANAGED_QUERY_KEYS = [...CANONICAL_QUERY_KEYS, ...LEGACY_QUERY_KEYS];
function parsePositiveInt(value) {
    if (typeof value !== 'string') {
        return undefined;
    }
    const parsed = Number.parseInt(value, 10);
    if (!Number.isFinite(parsed) || parsed <= 0) {
        return undefined;
    }
    return parsed;
}
function parseFiniteNumber(value) {
    if (typeof value !== 'string') {
        return undefined;
    }
    const parsed = Number.parseFloat(value);
    if (!Number.isFinite(parsed)) {
        return undefined;
    }
    return parsed;
}
function normalizePositiveInt(value) {
    if (value == null || Number.isNaN(value)) {
        return undefined;
    }
    const parsed = Math.floor(value);
    if (!Number.isFinite(parsed) || parsed <= 0) {
        return undefined;
    }
    return parsed;
}
function normalizeFiniteNumber(value) {
    if (value == null || Number.isNaN(value) || !Number.isFinite(value)) {
        return undefined;
    }
    return value;
}
function parseTextParam(value) {
    const normalized = value?.trim();
    return normalized ? normalized : undefined;
}
function parseDealType(value) {
    return value === 'RENT' ? 'RENT' : 'SALE';
}
const LOCATION_FILTER_TYPES_SET = new Set(LOCATION_FILTER_TYPES);
const LOCATION_RADIUS_VALUES_SET = new Set(LOCATION_RADIUS_VALUES);
const LOCATION_MODE_VALUES_SET = new Set(LOCATION_MODE_VALUES);
function parseLocationType(value) {
    if (typeof value !== 'string') {
        return undefined;
    }
    const normalized = value.trim().toUpperCase();
    if (normalized === 'PLACE') {
        return 'POINT';
    }
    if (!normalized || !LOCATION_FILTER_TYPES_SET.has(normalized)) {
        return undefined;
    }
    return normalized;
}
function parseRadiusKm(value) {
    const parsed = parsePositiveInt(value);
    if (parsed == null || !LOCATION_RADIUS_VALUES_SET.has(parsed)) {
        return undefined;
    }
    return parsed;
}
function parseLocationMode(value) {
    if (typeof value !== 'string') {
        return 'exact';
    }
    const normalized = value.trim().toLowerCase();
    if (!normalized || !LOCATION_MODE_VALUES_SET.has(normalized)) {
        return 'exact';
    }
    return normalized;
}
function parseSort(value) {
    if (typeof value !== 'string') {
        return undefined;
    }
    const normalized = value.trim();
    if (!normalized) {
        return undefined;
    }
    if (B2C_LISTINGS_SORT_VALUES.includes(normalized)) {
        return normalized;
    }
    return undefined;
}
function toNormalizedArray(values) {
    const uniq = new Set();
    for (const value of values) {
        const normalized = value.trim();
        if (!normalized) {
            continue;
        }
        uniq.add(normalized);
    }
    return Array.from(uniq);
}
function setNumberParam(queryParams, key, value) {
    if (value == null || Number.isNaN(value) || value <= 0) {
        return;
    }
    queryParams.set(key, String(value));
}
function setTextParam(queryParams, key, value) {
    const normalized = value?.trim();
    if (!normalized) {
        return;
    }
    queryParams.set(key, normalized);
}
function setCoordinateParam(queryParams, key, value) {
    if (value == null || Number.isNaN(value) || !Number.isFinite(value)) {
        return;
    }
    queryParams.set(key, String(Number(value.toFixed(6))));
}
function normalizeCountryCode(value) {
    return value?.trim() ? value.trim().toUpperCase() : undefined;
}
function normalizeProvinceCode(value) {
    return value?.trim() ? value.trim().toUpperCase() : undefined;
}
function resolveLocationTypeFromSuggestion(location) {
    const canonical = parseLocationType(location.locationType ?? null);
    if (canonical) {
        return canonical;
    }
    const fromLegacy = parseLocationType(location.type ?? null);
    if (fromLegacy) {
        return fromLegacy;
    }
    return 'POINT';
}
export function buildExactLocationParamsFromSuggestion(location) {
    const patch = location.addressPatch;
    return {
        locationMode: 'exact',
        uiLocationLabel: parseTextParam(location.shortLabel ?? null) ?? location.label,
        locationType: resolveLocationTypeFromSuggestion(location),
        locationLabel: parseTextParam(patch?.formattedAddress ?? null) ?? location.label,
        municipality: parseTextParam(patch?.municipality ?? null) ?? parseTextParam(location.municipality ?? null),
        locality: parseTextParam(patch?.locality ?? null) ?? parseTextParam(location.locality ?? null),
        city: parseTextParam(patch?.city ?? null) ?? parseTextParam(location.city ?? null),
        district: parseTextParam(patch?.district ?? null) ?? parseTextParam(location.district ?? null),
        postalCode: parseTextParam(patch?.postalCode ?? null) ?? parseTextParam(location.postalCode ?? null),
        province: parseTextParam(patch?.province ?? null) ?? parseTextParam(location.province ?? null),
        provinceCode: normalizeProvinceCode(patch?.provinceCode) ?? normalizeProvinceCode(location.provinceCode),
        region: parseTextParam(patch?.region ?? null) ?? parseTextParam(location.region ?? null),
        countryCode: normalizeCountryCode(patch?.countryCode) ?? normalizeCountryCode(location.countryCode),
        lat: undefined,
        lng: undefined,
        radiusKm: undefined,
        uiCenterLat: normalizeFiniteNumber(location.lat),
        uiCenterLng: normalizeFiniteNumber(location.lng),
    };
}
export function parseListingsFiltersFromUrl(searchParams) {
    const dealType = parseDealType(searchParams.get('deal') ?? searchParams.get('dealType'));
    const propertyTypesRaw = searchParams.getAll('propertyTypes');
    if (propertyTypesRaw.length === 0) {
        const legacyPropertyType = searchParams.get('propertyType');
        if (legacyPropertyType != null) {
            propertyTypesRaw.push(legacyPropertyType);
        }
    }
    const priceMin = parsePositiveInt(searchParams.get('priceMin')) ?? parsePositiveInt(searchParams.get('minPrice'));
    const priceMax = parsePositiveInt(searchParams.get('priceMax')) ?? parsePositiveInt(searchParams.get('maxPrice'));
    const areaMin = parsePositiveInt(searchParams.get('areaMin'));
    const areaMax = parsePositiveInt(searchParams.get('areaMax'));
    const roomsMin = parsePositiveInt(searchParams.get('roomsMin'));
    const roomsMax = parsePositiveInt(searchParams.get('roomsMax'));
    const bathroomsMin = parsePositiveInt(searchParams.get('bathroomsMin'));
    const bathroomsMax = parsePositiveInt(searchParams.get('bathroomsMax'));
    const page = parsePositiveInt(searchParams.get('page')) ?? 1;
    const sort = parseSort(searchParams.get('sort'));
    const municipality = parseTextParam(searchParams.get('municipality'));
    const locality = parseTextParam(searchParams.get('locality'));
    const city = parseTextParam(searchParams.get('city'));
    const district = parseTextParam(searchParams.get('district'));
    const postalCode = parseTextParam(searchParams.get('postalCode'));
    const province = parseTextParam(searchParams.get('province'));
    const provinces = toNormalizedArray(searchParams.getAll('provinces'));
    const provinceCode = normalizeProvinceCode(parseTextParam(searchParams.get('provinceCode')));
    const region = parseTextParam(searchParams.get('region'));
    const countryCode = normalizeCountryCode(parseTextParam(searchParams.get('countryCode')));
    const locationLabel = parseTextParam(searchParams.get('locationLabel'));
    const locationType = parseLocationType(searchParams.get('locationType'));
    const lat = parseFiniteNumber(searchParams.get('lat'));
    const lng = parseFiniteNumber(searchParams.get('lng'));
    const radiusKm = parseRadiusKm(searchParams.get('radiusKm'));
    const locationMode = parseLocationMode(searchParams.get('locationMode'));
    const uiLocationLabel = parseTextParam(searchParams.get('uiLocationLabel'));
    const uiCenterLat = parseFiniteNumber(searchParams.get('uiCenterLat'));
    const uiCenterLng = parseFiniteNumber(searchParams.get('uiCenterLng'));
    const propertyTypes = getListingPropertyTypeRequestValues(toNormalizedArray(propertyTypesRaw));
    const features = getListingFeatureRequestValues(toNormalizedArray(searchParams.getAll('features')));
    const params = {
        dealType,
        page,
        sort: sort ?? 'newest',
    };
    if (propertyTypes.length > 0) {
        params.propertyTypes = propertyTypes;
    }
    if (features.length > 0) {
        params.features = features;
    }
    if (priceMin != null)
        params.priceMin = priceMin;
    if (priceMax != null)
        params.priceMax = priceMax;
    if (areaMin != null)
        params.areaMin = areaMin;
    if (areaMax != null)
        params.areaMax = areaMax;
    if (roomsMin != null)
        params.roomsMin = roomsMin;
    if (roomsMax != null)
        params.roomsMax = roomsMax;
    if (bathroomsMin != null)
        params.bathroomsMin = bathroomsMin;
    if (bathroomsMax != null)
        params.bathroomsMax = bathroomsMax;
    if (municipality)
        params.municipality = municipality;
    if (locality)
        params.locality = locality;
    if (city)
        params.city = city;
    if (district)
        params.district = district;
    if (postalCode)
        params.postalCode = postalCode;
    if (province)
        params.province = province;
    if (provinces.length > 0)
        params.provinces = provinces;
    if (provinceCode)
        params.provinceCode = provinceCode;
    if (region)
        params.region = region;
    if (countryCode)
        params.countryCode = countryCode;
    if (locationLabel)
        params.locationLabel = locationLabel;
    if (locationType)
        params.locationType = locationType;
    if (lat != null)
        params.lat = lat;
    if (lng != null)
        params.lng = lng;
    if (radiusKm != null && lat != null && lng != null)
        params.radiusKm = radiusKm;
    params.locationMode = locationMode;
    if (uiLocationLabel)
        params.uiLocationLabel = uiLocationLabel;
    if (uiCenterLat != null)
        params.uiCenterLat = uiCenterLat;
    if (uiCenterLng != null)
        params.uiCenterLng = uiCenterLng;
    return {
        dealType,
        params,
    };
}
export function normalizeListingsUrlParams(params, dealType) {
    const normalized = {
        dealType,
        page: normalizePositiveInt(params.page) ?? 1,
        sort: parseSort(params.sort ?? null) ?? 'newest',
    };
    const propertyTypesSource = Array.isArray(params.propertyTypes) && params.propertyTypes.length > 0
        ? params.propertyTypes
        : params.propertyType != null
            ? [params.propertyType]
            : [];
    const propertyTypes = getListingPropertyTypeRequestValues(toNormalizedArray(propertyTypesSource));
    const features = getListingFeatureRequestValues(toNormalizedArray(params.features ?? []));
    if (propertyTypes.length > 0) {
        normalized.propertyTypes = propertyTypes;
    }
    if (features.length > 0) {
        normalized.features = features;
    }
    const priceMin = normalizePositiveInt(params.priceMin ?? params.minPrice);
    const priceMax = normalizePositiveInt(params.priceMax ?? params.maxPrice);
    const areaMin = normalizePositiveInt(params.areaMin);
    const areaMax = normalizePositiveInt(params.areaMax);
    const roomsMin = normalizePositiveInt(params.roomsMin);
    const roomsMax = normalizePositiveInt(params.roomsMax);
    const bathroomsMin = normalizePositiveInt(params.bathroomsMin);
    const bathroomsMax = normalizePositiveInt(params.bathroomsMax);
    if (priceMin != null) {
        normalized.priceMin = priceMin;
        normalized.minPrice = priceMin;
    }
    if (priceMax != null) {
        normalized.priceMax = priceMax;
        normalized.maxPrice = priceMax;
    }
    if (areaMin != null)
        normalized.areaMin = areaMin;
    if (areaMax != null)
        normalized.areaMax = areaMax;
    if (roomsMin != null)
        normalized.roomsMin = roomsMin;
    if (roomsMax != null)
        normalized.roomsMax = roomsMax;
    if (bathroomsMin != null)
        normalized.bathroomsMin = bathroomsMin;
    if (bathroomsMax != null)
        normalized.bathroomsMax = bathroomsMax;
    const locationMode = parseLocationMode(params.locationMode ?? null);
    normalized.locationMode = locationMode;
    const uiLocationLabel = parseTextParam(params.uiLocationLabel ?? null);
    if (uiLocationLabel) {
        normalized.uiLocationLabel = uiLocationLabel;
    }
    const uiCenterLat = normalizeFiniteNumber(params.uiCenterLat);
    const uiCenterLng = normalizeFiniteNumber(params.uiCenterLng);
    if (uiCenterLat != null)
        normalized.uiCenterLat = uiCenterLat;
    if (uiCenterLng != null)
        normalized.uiCenterLng = uiCenterLng;
    const locationType = parseLocationType(params.locationType ?? null);
    const lat = normalizeFiniteNumber(params.lat);
    const lng = normalizeFiniteNumber(params.lng);
    const radiusKm = params.radiusKm != null ? parseRadiusKm(String(params.radiusKm)) : undefined;
    if (locationMode === 'radius') {
        if (lat != null)
            normalized.lat = lat;
        if (lng != null)
            normalized.lng = lng;
        if (radiusKm != null && lat != null && lng != null) {
            normalized.radiusKm = radiusKm;
        }
        return normalized;
    }
    if (params.municipality?.trim()) {
        normalized.municipality = params.municipality.trim();
    }
    if (params.locality?.trim()) {
        normalized.locality = params.locality.trim();
    }
    if (params.city?.trim()) {
        normalized.city = params.city.trim();
    }
    if (params.district?.trim()) {
        normalized.district = params.district.trim();
    }
    if (params.postalCode?.trim()) {
        normalized.postalCode = params.postalCode.trim();
    }
    const provinces = toNormalizedArray(params.provinces ?? []);
    if (provinces.length > 0) {
        normalized.provinces = provinces;
        if (provinces.length === 1) {
            normalized.province = provinces[0];
        }
    }
    else if (params.province?.trim()) {
        normalized.province = params.province.trim();
    }
    if (params.provinceCode?.trim()) {
        normalized.provinceCode = params.provinceCode.trim().toUpperCase();
    }
    if (params.region?.trim()) {
        normalized.region = params.region.trim();
    }
    if (params.countryCode?.trim()) {
        normalized.countryCode = params.countryCode.trim().toUpperCase();
    }
    if (params.locationLabel?.trim()) {
        normalized.locationLabel = params.locationLabel.trim();
    }
    if (locationType) {
        normalized.locationType = locationType;
    }
    return normalized;
}
export function buildListingsUrlSearchParams(baseSearchParams, filters) {
    const next = new URLSearchParams(baseSearchParams);
    for (const key of MANAGED_QUERY_KEYS) {
        next.delete(key);
    }
    next.set('deal', filters.dealType);
    const propertyTypes = getListingPropertyTypeRequestValues(filters.params.propertyTypes ?? []);
    for (const propertyType of propertyTypes) {
        next.append('propertyTypes', propertyType);
    }
    const features = getListingFeatureRequestValues(filters.params.features ?? []);
    for (const feature of features) {
        next.append('features', feature);
    }
    setNumberParam(next, 'priceMin', filters.params.priceMin);
    setNumberParam(next, 'priceMax', filters.params.priceMax);
    setNumberParam(next, 'areaMin', filters.params.areaMin);
    setNumberParam(next, 'areaMax', filters.params.areaMax);
    setNumberParam(next, 'roomsMin', filters.params.roomsMin);
    setNumberParam(next, 'roomsMax', filters.params.roomsMax);
    setNumberParam(next, 'bathroomsMin', filters.params.bathroomsMin);
    setNumberParam(next, 'bathroomsMax', filters.params.bathroomsMax);
    const locationMode = parseLocationMode(filters.params.locationMode ?? null);
    next.set('locationMode', locationMode);
    setTextParam(next, 'uiLocationLabel', filters.params.uiLocationLabel);
    setCoordinateParam(next, 'uiCenterLat', normalizeFiniteNumber(filters.params.uiCenterLat));
    setCoordinateParam(next, 'uiCenterLng', normalizeFiniteNumber(filters.params.uiCenterLng));
    if (locationMode === 'radius') {
        const lat = normalizeFiniteNumber(filters.params.lat);
        const lng = normalizeFiniteNumber(filters.params.lng);
        setCoordinateParam(next, 'lat', lat);
        setCoordinateParam(next, 'lng', lng);
        const radiusKm = filters.params.radiusKm != null ? parseRadiusKm(String(filters.params.radiusKm)) : undefined;
        if (radiusKm != null && lat != null && lng != null) {
            next.set('radiusKm', String(radiusKm));
        }
    }
    else {
        setTextParam(next, 'municipality', filters.params.municipality);
        setTextParam(next, 'locality', filters.params.locality);
        setTextParam(next, 'city', filters.params.city);
        setTextParam(next, 'district', filters.params.district);
        setTextParam(next, 'postalCode', filters.params.postalCode);
        const provinces = toNormalizedArray(filters.params.provinces ?? []);
        if (provinces.length === 1) {
            setTextParam(next, 'province', filters.params.province ?? provinces[0]);
        }
        else if (provinces.length === 0) {
            setTextParam(next, 'province', filters.params.province);
        }
        for (const province of provinces) {
            next.append('provinces', province);
        }
        setTextParam(next, 'provinceCode', filters.params.provinceCode?.toUpperCase());
        setTextParam(next, 'region', filters.params.region);
        setTextParam(next, 'countryCode', filters.params.countryCode?.toUpperCase());
        setTextParam(next, 'locationLabel', filters.params.locationLabel);
        const locationType = parseLocationType(filters.params.locationType ?? null);
        if (locationType) {
            next.set('locationType', locationType);
        }
    }
    next.set('page', String(Math.max(1, filters.params.page ?? 1)));
    next.set('sort', parseSort(filters.params.sort ?? null) ?? 'newest');
    return next;
}
export function buildSelectedLocationFromFilters(params) {
    const locationMode = parseLocationMode(params.locationMode ?? null);
    const locationLabel = parseTextParam(params.uiLocationLabel ?? null) ?? parseTextParam(params.locationLabel ?? null);
    const locationType = parseLocationType(params.locationType ?? null) ?? 'POINT';
    const centerLat = normalizeFiniteNumber(params.uiCenterLat);
    const centerLng = normalizeFiniteNumber(params.uiCenterLng);
    if (!locationLabel || centerLat == null || centerLng == null) {
        return null;
    }
    const radiusLat = normalizeFiniteNumber(params.lat);
    const radiusLng = normalizeFiniteNumber(params.lng);
    const municipality = parseTextParam(params.municipality ?? null);
    const locality = parseTextParam(params.locality ?? null);
    const city = parseTextParam(params.city ?? null);
    const district = parseTextParam(params.district ?? null);
    const postalCode = parseTextParam(params.postalCode ?? null);
    const provinces = toNormalizedArray(params.provinces ?? []);
    const province = parseTextParam(params.province ?? null) ?? (provinces.length === 1 ? provinces[0] : undefined);
    const provinceCode = normalizeProvinceCode(parseTextParam(params.provinceCode ?? null));
    const region = parseTextParam(params.region ?? null);
    const countryCode = normalizeCountryCode(parseTextParam(params.countryCode ?? null));
    return {
        id: `url-location:${locationType}:${locationLabel}`,
        label: locationLabel,
        shortLabel: parseTextParam(params.uiLocationLabel ?? null),
        locationType,
        type: locationType,
        municipality,
        locality,
        city,
        district,
        postalCode,
        province,
        provinceCode,
        region,
        countryCode,
        addressPatch: {
            municipality,
            locality,
            city,
            district,
            postalCode,
            province: province ?? (provinces.length === 1 ? provinces[0] : undefined),
            provinceCode,
            region,
            countryCode,
            formattedAddress: parseTextParam(params.locationLabel ?? null),
            geo: {
                lat: centerLat,
                lng: centerLng,
            },
        },
        lat: locationMode === 'radius' ? (radiusLat ?? centerLat) : centerLat,
        lng: locationMode === 'radius' ? (radiusLng ?? centerLng) : centerLng,
    };
}
function resolveListingsUrl(pathOrUrl) {
    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://example.local';
    return new URL(pathOrUrl, origin);
}
export function buildListingsSearchUrlFromLocation(listingsPathOrUrl, location) {
    const fallbackPath = '/listings';
    const source = listingsPathOrUrl?.trim() || fallbackPath;
    const url = resolveListingsUrl(source);
    const baseSearchParams = new URLSearchParams(url.search);
    const parsed = parseListingsFiltersFromUrl(baseSearchParams);
    const dealType = parsed.dealType;
    const nextParams = normalizeListingsUrlParams({
        dealType,
        page: 1,
        sort: 'newest',
        ...buildExactLocationParamsFromSuggestion(location),
    }, dealType);
    const nextSearchParams = buildListingsUrlSearchParams(baseSearchParams, {
        dealType,
        params: nextParams,
    });
    const queryString = nextSearchParams.toString();
    return `${url.pathname}${queryString ? `?${queryString}` : ''}${url.hash}`;
}
